import React, { useEffect, useRef, useState, type RefObject } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { logoSets } from '../data/clientLogos';
import { EASE_SOFT } from '../utils/reveal';
import { LogoMark, LogoWhiteFilter, loadLogo } from './LogoMark';
interface LogoWallProps {
  /** The tagline above — logos appear once it's risen to 30% from the top of the screen */
  triggerRef: RefObject<HTMLElement>;
}

/** Trigger line: tagline top at this fraction of the viewport height */
const APPEAR_AT = 0.3;
/**
 * Clay-style rhythm: one steady beat, and on each beat exactly ONE slot swaps.
 * The slot is random, but never the same slot twice in a row, so the change wanders across the row.
 */
const BEAT_MS = 1800;
/** First swap waits a little longer after the row has settled in */
const FIRST_BEAT_MS = 2600;
/** Desktop: two rows of five. Phones: a 3 × 3 square of nine */
const DESKTOP = {
  columns: 5,
  slots: 10
};
const MOBILE = {
  columns: 3,
  slots: 9
};
const MOBILE_QUERY = '(max-width: 767px)';
/** Soft blur cross-swap */
const SWAP_EASE_SOFT = [0.45, 0, 0.25, 1] as const;

/** Every logo, in one pool — slots draw from it */
const POOL = logoSets.flat();
/** Entrance: each logo blurs in on its own, left to right, top row first, rising slightly */
const ENTRANCE_STAGGER = 0.12;

/**
 * Always exactly five white client logos in one row, spread edge to edge across the screen.
 * The row softly rises into focus once the tagline reaches 30% from the top; afterwards, on a steady
 * beat, one logo at a time blurs out and the next blurs in.
 */
export function LogoWall({
  triggerRef
}: LogoWallProps) {
  const reduce = useReducedMotion();
  const [shown, setShown] = useState(false);
  const [mobile, setMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const {
    columns: COLUMNS,
    slots: SLOTS
  } = mobile ? MOBILE : DESKTOP;
  // Pool index per slot — start with the first set
  const [slots, setSlots] = useState<number[]>(() => Array.from({
    length: SLOTS
  }, (_, i) => i));
  // Layout changed (rotate / resize across the breakpoint): keep the logos on screen, add or drop slots
  useEffect(() => {
    setSlots(cur => {
      if (cur.length === SLOTS) return cur;
      if (cur.length > SLOTS) return cur.slice(0, SLOTS);
      const next = [...cur];
      for (let i = 0; next.length < SLOTS && i < POOL.length; i++) if (!next.includes(i)) next.push(i);
      return next;
    });
  }, [SLOTS]);
  const rowRef = useRef<HTMLDivElement>(null);

  // Load every logo up front, so swaps never wait on the network
  useEffect(() => {
    POOL.forEach(l => loadLogo(l.src));
  }, []);

  // Appear once the tagline crosses the trigger line
  useEffect(() => {
    if (shown) return;
    let raf = 0;
    const check = () => {
      const el = triggerRef.current;
      if (el && el.getBoundingClientRect().top <= window.innerHeight * APPEAR_AT) setShown(true);else raf = requestAnimationFrame(check);
    };
    raf = requestAnimationFrame(check);
    return () => cancelAnimationFrame(raf);
  }, [shown, triggerRef]);

  // One steady beat; each beat swaps a single random slot (only while the row is on screen)
  useEffect(() => {
    if (!shown) return;
    let visible = true;
    const io = new IntersectionObserver(([entry]) => visible = entry.isIntersecting);
    if (rowRef.current) io.observe(rowRef.current);

    // Each row has its own shuffle bag of columns: every column swaps exactly once per round, in a fresh
    // random order, and a new round never starts with the column that ended the previous one.
    // On each beat both rows swap together — never in the same column, so the change reads as a diagonal.
    const rows = SLOTS / COLUMNS;
    const bags: number[][] = Array.from({
      length: rows
    }, () => []);
    const lastCol: number[] = Array.from({
      length: rows
    }, () => -1);
    const shuffle = () => {
      const order = Array.from({
        length: COLUMNS
      }, (_, i) => i);
      for (let i = order.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
      }
      return order;
    };
    const takeColumn = (row: number, avoid: number[]) => {
      if (!bags[row].length) {
        const order = shuffle();
        if (order[0] === lastCol[row]) [order[0], order[1]] = [order[1], order[0]];
        bags[row] = order;
      }
      // Prefer a column the other rows aren't using this beat
      const pick = bags[row].findIndex(c => !avoid.includes(c));
      const [col] = bags[row].splice(pick === -1 ? 0 : pick, 1);
      lastCol[row] = col;
      return col;
    };
    let timer = 0;
    const beat = () => {
      if (visible && !document.hidden) {
        const used: number[] = [];
        const slotsThisBeat = Array.from({
          length: rows
        }, (_, row) => {
          const col = takeColumn(row, used);
          used.push(col);
          return row * COLUMNS + col;
        });
        setSlots(cur => {
          const next = [...cur];
          slotsThisBeat.forEach(slot => {
            // Any logo not already on screen (including ones just placed this beat)
            const free = POOL.map((_, i) => i).filter(i => !next.includes(i));
            if (free.length) next[slot] = free[Math.floor(Math.random() * free.length)];
          });
          return next;
        });
      }
      timer = window.setTimeout(beat, BEAT_MS);
    };
    timer = window.setTimeout(beat, FIRST_BEAT_MS);
    return () => {
      window.clearTimeout(timer);
      io.disconnect();
    };
  }, [shown, COLUMNS, SLOTS]);
  return <div ref={rowRef} aria-label="Clients" role="list"
  // Equal columns (5 × 2 on desktop, 3 × 3 on phones): every logo is centred on a fixed point
  className="mt-[clamp(15rem,calc(30*var(--vw)),33rem)] grid w-full items-center gap-x-6 gap-y-12 px-6 md:gap-x-[calc(3*var(--vw))] md:gap-y-[clamp(2.5rem,calc(6*var(--vw)),6rem)] md:px-[clamp(0.75rem,calc(1.4*var(--vw)),1.5rem)]" style={{
    gridTemplateColumns: `repeat(${COLUMNS}, minmax(0, 1fr))`
  }} data-id="element-141" data-mp="2yj7jirs68k2o">
      <LogoWhiteFilter data-id="element-142" data-mp="jgxe4uyr4ehk" />
      {slots.map((poolIndex, slot) => {
      const logo = POOL[poolIndex];
      const align = 'justify-center';
      return (
        // Fixed slot width keeps exactly 5 across; outgoing and incoming logos overlap in place
        // Each slot rises from below and comes into focus on its own beat
        <motion.div key={slot} role="listitem" className="relative h-8 w-full md:h-[clamp(1.3rem,calc(3.25*var(--vw)),3.6rem)]" initial={reduce ? false : {
          opacity: 0,
          filter: 'blur(6px)',
          y: 12
        }} animate={shown ? {
          opacity: 1,
          filter: 'blur(0px)',
          y: 0,
          transitionEnd: {
            filter: 'none'
          }
        } : undefined} transition={{
          delay: slot * ENTRANCE_STAGGER,
          opacity: {
            duration: 1.1,
            ease: EASE_SOFT,
            delay: slot * ENTRANCE_STAGGER
          },
          filter: {
            duration: 1.2,
            ease: EASE_SOFT,
            delay: slot * ENTRANCE_STAGGER
          },
          y: {
            duration: 1.3,
            ease: EASE_SOFT,
            delay: slot * ENTRANCE_STAGGER
          }
        }} data-id="element-143" data-mp="xhrovk99urje" data-mpi={slot}>
            <AnimatePresence initial={false} data-id="element-144" data-mp="3albqjz2xwvqo">
              <motion.div key={logo.src} className={`absolute inset-0 flex items-center ${align}`}
            // Same blur switch as the tagline: old drifts up and blurs out, new rises into focus
            // Slow, soft cross-blur: the old logo starts dissolving first, the new one resolves just behind it
            initial={reduce ? {
              opacity: 0
            } : {
              opacity: 0,
              filter: 'blur(10px)',
              y: 5
            }} animate={{
              opacity: 1,
              filter: 'blur(0px)',
              y: 0,
              transitionEnd: {
                filter: 'none'
              },
              transition: reduce ? {
                duration: 0
              } : {
                duration: 0.9,
                ease: SWAP_EASE_SOFT,
                delay: 0.15
              }
            }} exit={reduce ? {
              opacity: 0,
              transition: {
                duration: 0
              }
            } : {
              opacity: 0,
              filter: 'blur(10px)',
              y: -5,
              transition: {
                duration: 0.8,
                ease: SWAP_EASE_SOFT
              }
            }} data-id="element-145" data-mp="35o25swqhx896">
                <LogoMark logo={logo} data-id="element-146" data-mp="3l8h5xs19x4bk" />
              </motion.div>
            </AnimatePresence>
          </motion.div>
      );
    })}
    </div>;
}